import React, { ReactNode, KeyboardEvent } from 'react';

export interface DataCardField {
    label: string;
    value: ReactNode;
}

export interface DataCardProps {
    title: ReactNode;
    subtitle?: ReactNode;
    fields?: DataCardField[];
    actions?: ReactNode;
    onClick?: () => void;
    accent?: 'default' | 'highlight' | 'muted';
    className?: string;
}

const accentClasses: Record<NonNullable<DataCardProps['accent']>, string> = {
    default: 'bg-white border-gray-200',
    highlight: 'bg-blue-50/40 border-blue-200',
    muted: 'bg-slate-50/60 border-slate-200',
};

const DataCard: React.FC<DataCardProps> = ({
    title,
    subtitle,
    fields,
    actions,
    onClick,
    accent = 'default',
    className = '',
}) => {
    const interactive = !!onClick;
    const handleKey = (e: KeyboardEvent<HTMLDivElement>) => {
        if (!interactive) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.();
        }
    };

    return (
        <div
            role={interactive ? 'button' : undefined}
            tabIndex={interactive ? 0 : undefined}
            onClick={onClick}
            onKeyDown={handleKey}
            className={`rounded-xl border shadow-sm p-4 transition-colors ${accentClasses[accent]} ${interactive ? 'cursor-pointer hover:border-brand-300 hover:shadow' : ''} ${className}`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="font-bold text-gray-900 leading-snug break-words">{title}</div>
                    {subtitle && (
                        <div className="text-sm text-gray-500 mt-0.5 break-words">{subtitle}</div>
                    )}
                </div>
            </div>

            {fields && fields.length > 0 && (
                <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                    {fields.map((f, i) => (
                        <div key={i} className="min-w-0">
                            <dt className="text-[10px] uppercase font-bold tracking-wider text-gray-400">{f.label}</dt>
                            <dd className="text-gray-700 break-words">{f.value}</dd>
                        </div>
                    ))}
                </dl>
            )}

            {actions && (
                <div
                    className="mt-3 pt-3 border-t border-gray-100 flex justify-end gap-2"
                    onClick={(e) => e.stopPropagation()}
                >
                    {actions}
                </div>
            )}
        </div>
    );
};

export default DataCard;
